/**
 * Inline Quick Add - Final stable fix for Horizon
 * Choose -> variant -> Add = drawer open + product add + smooth update (no reload)
 */

class InlineQuickAdd {
  constructor(card) {
    this.card = card;
    this.overlay = card.querySelector('[data-inline-quick-add]');
    if (!this.overlay) return;

    this.trigger = card.querySelector('[data-inline-quick-add-trigger]');
    this.closeBtn = this.overlay.querySelector('[data-inline-close]');
    this.cancelBtn = this.overlay.querySelector('[data-inline-cancel]');
    this.form = this.overlay.querySelector('[data-inline-form]');
    this.submitBtn = this.overlay.querySelector('[data-inline-submit]');
    this.variantIdInput = this.overlay.querySelector('[data-variant-id]');
    this.qtyInput = this.overlay.querySelector('[data-qty-input]');
    this.qtyMinus = this.overlay.querySelector('[data-qty-minus]');
    this.qtyPlus = this.overlay.querySelector('[data-qty-plus]');
    this.optionInputs = Array.from(this.overlay.querySelectorAll('[data-inline-option]'));

    const jsonScript = this.overlay.querySelector('[data-variants-json]');
    try {
      this.variants = jsonScript ? JSON.parse(jsonScript.textContent) : [];
    } catch (e) {
      this.variants = [];
    }

    this.isSubmitting = false;
    this.bindEvents();
    this.updateSelectedVariant();
  }

  get root() {
    return window.Shopify?.routes?.root || '/';
  }

  bindEvents() {
    this.trigger?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.open();
    });

    this.closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    });

    this.cancelBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    });

    this.overlay.addEventListener('click', (e) => e.stopPropagation());

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.updateSelectedVariant());
      input.addEventListener('click', (e) => {
        if (input.dataset.optionAvailable === 'false') e.preventDefault();
      });
    });

    this.qtyMinus?.addEventListener('click', (e) => {
      e.preventDefault();
      this.changeQty(-1);
    });

    this.qtyPlus?.addEventListener('click', (e) => {
      e.preventDefault();
      this.changeQty(1);
    });

    this.qtyInput?.addEventListener('change', () => {
      const val = parseInt(this.qtyInput.value, 10);
      if (isNaN(val) || val < 1) this.qtyInput.value = '1';
    });

    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.addToCart();
    });

    this.submitBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.addToCart();
    });
  }

  open() {
    document.querySelectorAll('[data-inline-quick-add][aria-hidden="false"]').forEach((el) => {
      if (el !== this.overlay) {
        el.setAttribute('aria-hidden', 'true');
        el.closest('product-card')?.removeAttribute('data-inline-open');
      }
    });

    this.overlay.setAttribute('aria-hidden', 'false');
    this.card.setAttribute('data-inline-open', 'true');
    this.updateSelectedVariant();
  }

  close() {
    this.overlay.setAttribute('aria-hidden', 'true');
    this.card.removeAttribute('data-inline-open');
  }

  changeQty(delta) {
    if (!this.qtyInput) return;
    const current = parseInt(this.qtyInput.value, 10) || 1;
    this.qtyInput.value = String(Math.max(1, current + delta));
  }

  getSelectedValues() {
    const map = new Map();
    this.optionInputs.forEach((input) => {
      const key = input.name || 'option';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(input);
    });

    const values = [];
    map.forEach((inputs) => {
      const checked = inputs.find((i) => i.checked);
      if (checked) values.push(checked.value);
    });
    return values;
  }

  findVariant() {
    const selected = this.getSelectedValues();
    if (!this.variants?.length) return null;

    let match = this.variants.find(
      (v) =>
        Array.isArray(v.options) &&
        v.options.length === selected.length &&
        v.options.every((opt, i) => String(opt) === String(selected[i]))
    );
    if (match) return match;

    match = this.variants.find((v) => {
      return (
        (selected[0] == null || String(v.option1) === String(selected[0])) &&
        (selected[1] == null || String(v.option2) === String(selected[1])) &&
        (selected[2] == null || String(v.option3) === String(selected[2]))
      );
    });
    if (match) return match;

    return this.variants.length === 1 ? this.variants[0] : null;
  }

  updateSelectedVariant() {
    const variant = this.findVariant();

    this.optionInputs.forEach((input) => {
      input.closest('label')?.classList.toggle('is-selected', !!input.checked);
    });

    this.overlay.querySelectorAll('.inline-quick-add__option, [data-option-wrapper]').forEach((wrap) => {
      const checked = wrap.querySelector('[data-inline-option]:checked');
      const label = wrap.querySelector('[data-selected-value]');
      if (checked && label) label.textContent = checked.value;
    });

    if (!variant) {
      if (this.submitBtn) this.submitBtn.disabled = true;
      this.setBtnText('Unavailable');
      return null;
    }

    if (this.variantIdInput) this.variantIdInput.value = String(variant.id);

    if (variant.available === false) {
      if (this.submitBtn) this.submitBtn.disabled = true;
      this.setBtnText('Sold out');
    } else {
      if (this.submitBtn) this.submitBtn.disabled = false;
      this.setBtnText('Add');
    }

    return variant;
  }

  setBtnText(text) {
    if (!this.submitBtn) return;
    const el = this.submitBtn.querySelector('[data-btn-text]');
    if (el) el.textContent = text;
    else this.submitBtn.textContent = text;
  }

  getSectionIds() {
    const ids = new Set();

    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });

    document.querySelectorAll('cart-drawer-component, cart-icon, cart-icon-component').forEach((el) => {
      const section = el.closest('[id^="shopify-section-"]');
      if (section?.id) ids.add(section.id.replace('shopify-section-', ''));
    });

    return [...ids];
  }

  openDrawer() {
    const drawer =
      document.querySelector('cart-drawer-component')?.closest('theme-drawer') ||
      document.querySelector('theme-drawer#CartDrawer') ||
      document.querySelector('theme-drawer[id*="cart" i]') ||
      document.querySelector('theme-drawer');

    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  async morphSections(sections) {
    if (!sections || typeof sections !== 'object') return false;

    try {
      const { morphSection } = await import('@theme/section-renderer');
      Object.entries(sections).forEach(([sectionId, html]) => {
        if (!sectionId || !html) return;
        if (!document.getElementById(`shopify-section-${sectionId}`)) return;
        morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      });
      return true;
    } catch (e) {
      // fallback soft replace (no full reload)
      Object.entries(sections).forEach(([sectionId, html]) => {
        const current = document.getElementById(`shopify-section-${sectionId}`);
        if (!current || !html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next =
          doc.getElementById(`shopify-section-${sectionId}`) ||
          doc.querySelector('.shopify-section') ||
          doc.body.firstElementChild;
        if (!next) return;

        // replace only inner content to reduce flicker
        current.innerHTML = next.innerHTML;
      });
      return true;
    }
  }

  updateCartCount(count) {
    const n = Number(count) || 0;
    document.querySelectorAll('cart-icon, cart-icon-component').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (typeof el.updateCount === 'function') el.updateCount(n);
      el.querySelectorAll('[ref="cartCount"], [data-cart-count], .cart-bubble__text, .cart-count').forEach((node) => {
        if (node instanceof HTMLElement) {
          node.textContent = String(n);
          node.hidden = n <= 0;
        }
      });
    });
  }

  async addToCart() {
    if (this.isSubmitting) return;

    const variant = this.updateSelectedVariant();
    const variantId = parseInt(String(this.variantIdInput?.value || variant?.id || ''), 10);
    const quantity = Math.max(1, parseInt(this.qtyInput?.value || '1', 10) || 1);

    if (!variantId) {
      console.error('[inline-quick-add] invalid variant id');
      this.setBtnText('Unavailable');
      return;
    }

    this.isSubmitting = true;
    if (this.submitBtn) {
      this.submitBtn.classList.add('inline-quick-add__btn--loading');
      this.submitBtn.disabled = true;
    }

    // 1) drawer open immediately
    this.openDrawer();

    try {
      const sectionIds = this.getSectionIds();

      // 2) native event modules
      const { CartLinesUpdateEvent, StandardEvents } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();

      // fire native event so cart-drawer + cart-items both listen
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity }],
          promise: deferred.promise,
        })
      );

      // also ensure open if auto-open misses
      this.openDrawer();

      // 3) add item
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', String(quantity));
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const addRes = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: formData,
      });

      const addJson = await addRes.json();
      if (!addRes.ok || addJson.status) {
        const msg = addJson.description || addJson.message || 'Add failed';
        deferred.reject(new Error(msg));
        throw new Error(msg);
      }

      // 4) get fresh cart
      const cartRes = await fetch(`${this.root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartRes.json();

      // 5) resolve native event (smooth path)
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: addJson.sections || {},
          source: 'inline-quick-add',
          didError: false,
        },
      });

      // 6) hard-guarantee section update without page reload
      if (addJson.sections) {
        await this.morphSections(addJson.sections);
      }

      // 7) count update
      this.updateCartCount(cart.item_count);

      // compatibility events
      document.dispatchEvent(
        new CustomEvent(StandardEvents.cartLinesUpdate || 'cart-lines:update', {
          bubbles: true,
          detail: { cart, sections: addJson.sections, source: 'inline-quick-add' },
        })
      );

      this.close();
      this.setBtnText('Add');
    } catch (err) {
      console.error('[inline-quick-add]', err);
      this.setBtnText('Try again');
      setTimeout(() => this.setBtnText('Add'), 1500);
    } finally {
      this.isSubmitting = false;
      if (this.submitBtn) {
        this.submitBtn.classList.remove('inline-quick-add__btn--loading');
        this.updateSelectedVariant();
      }
    }
  }
}

function relocateQuickAddButtons() {
  document.querySelectorAll('product-card').forEach((card) => {
    if (card.__quickAddRelocated) return;
    const quickAdd = card.querySelector('quick-add-component');
    const cardContent = card.querySelector('.product-card__content');
    if (!quickAdd || !cardContent) return;
    if (quickAdd.parentElement === cardContent) {
      card.__quickAddRelocated = true;
      return;
    }
    cardContent.appendChild(quickAdd);
    card.__quickAddRelocated = true;
  });
}

function initInlineQuickAdd() {
  document.querySelectorAll('product-card.product-card--has-inline-quick-add').forEach((card) => {
    if (card.__inlineQuickAddInitialized) return;
    card.__inlineQuickAddInitialized = true;
    new InlineQuickAdd(card);
  });
}

function initAll() {
  relocateQuickAddButtons();
  initInlineQuickAdd();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (node.nodeType !== 1) continue;
      if (node.matches?.('product-card') || node.querySelector?.('product-card')) {
        initAll();
        return;
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });